// ─── VillaGest — Supabase CRUD helpers ──────────────────────────────────────
// All functions return plain JS objects (camelCase) mapped from DB snake_case.
// Throws on error so callers can catch and show toasts.

import { supabase } from "./supabase";
import { createClient } from "@supabase/supabase-js";
import { DEMO_CONDO_NAME } from "../constants";

// ─── Row mappers ─────────────────────────────────────────────────────────────

const mapTicket = (r) => ({
  id:             r.id,
  title:          r.title,
  desc:           r.description,
  priority:       r.priority,
  status:         r.status,
  owner:          r.owner,
  ownerName:      r.owner_name,
  createdAt:      r.created_at,
  created:        r.created_at ? r.created_at.slice(0, 10) : null,
  closedAt:       r.closed_at ? r.closed_at.slice(0, 10) : null,
  history:        r.history ?? [],
  attachmentUrl:  r.attachment_url,
  attachmentName: r.attachment_name,
  attachments:    r.attachment_url
    ? [{ name: r.attachment_name, url: r.attachment_url }]
    : [],
});

const mapProfile = (r) => ({
  id:                 r.id,
  name:               r.name,
  email:              r.email,
  phone:              r.phone,
  fracao:             r.fracao,
  role:               r.role,
  photo:              r.photo_url,
  active:             r.active,
  isSuperAdmin:       !!r.is_super_admin,
  mustChangePassword: !!r.must_change_password,
  lastSignInAt:       r.last_sign_in_at || null,
});

const mapDoc = (r) => ({
  id:          r.id,
  name:        r.name,
  category:    r.category,
  size:        r.size ? fmtSize(r.size) : "—",
  sizeBytes:   r.size,
  date:        r.date,
  uploadedBy:  r.uploaded_by,
  description: r.description,
  tags:        r.tags ?? [],
  file:        null,          // no in-memory File object with Supabase
  fileUrl:     r.file_url,
  extractedText: r.extracted_text || "",
  archived:    r.archived,
});

const mapSupplier = (r) => ({
  id:      r.id,
  name:    r.name,
  service: r.service,
  address: r.address,
  phone:   r.phone,
  email:   r.email,
  notes:   r.notes,
});

const mapCondo = (r) => ({
  id:         r.id,
  name:       r.name,
  morada:     r.morada,
  numFracoes: r.num_fracoes,
  active:     r.active ?? true,
});

// ─── Util ─────────────────────────────────────────────────────────────────────

const fmtSize = (bytes) => {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const throw_if = (error, msg) => {
  if (error) throw new Error(msg || error.message);
};

// ─── CONDOMINIUMS ─────────────────────────────────────────────────────────────

export async function fetchUserCondominiums(userId) {
  const { data, error } = await supabase
    .from("condominium_users")
    .select("*, condominiums(*)")
    .eq("user_id", userId);
  throw_if(error, "Erro ao carregar condomínios");
  return data
    .filter(cu => cu.condominiums?.active !== false)
    .map(cu => ({
      ...mapCondo(cu.condominiums),
      role:       cu.role,
      fracao:     cu.fracao,
      permilagem: cu.permilagem ?? null,
    }));
}

// ─── TICKETS ──────────────────────────────────────────────────────────────────

export async function fetchTickets(condominiumId) {
  let q = supabase
    .from("tickets")
    .select("*")
    .order("created_at", { ascending: false });
  if (condominiumId) q = q.eq("condominium_id", condominiumId);
  const { data, error } = await q;
  throw_if(error, "Erro ao carregar ocorrências");
  return data.map(mapTicket);
}

export async function createTicket({ title, desc, priority, owner, ownerName, attachFile, condominiumId }) {
  let attachment_url = null;
  let attachment_name = null;

  // Upload attachment if provided — store path (not public URL) for private bucket
  if (attachFile) {
    const prefix = condominiumId ? `${condominiumId}/` : "";
    const path = `${prefix}tickets/${Date.now()}-${attachFile.name}`;
    const { error: upErr } = await supabase.storage
      .from("documents")
      .upload(path, attachFile, { upsert: false });
    throw_if(upErr, "Erro ao fazer upload do anexo");
    attachment_url = path;
    attachment_name = attachFile.name;
  }

  const now = new Date().toISOString().slice(0, 10);
  const row = {
    title,
    description: desc,
    priority,
    status: "Aberto",
    owner,
    owner_name: ownerName,
    history: [{ date: now, msg: "Ocorrência criada" }],
    attachment_url,
    attachment_name,
  };
  if (condominiumId) row.condominium_id = condominiumId;

  const { data, error } = await supabase
    .from("tickets")
    .insert(row)
    .select()
    .single();
  throw_if(error, "Erro ao criar ocorrência");
  return mapTicket(data);
}

export async function updateTicketStatus(id, status, currentHistory) {
  const now = new Date().toISOString().slice(0, 10);
  const newHistory = [...(currentHistory ?? []), { date: now, msg: `Estado: "${status}"` }];
  const patch = {
    status,
    history: newHistory,
    ...(status === "Concluído" ? { closed_at: new Date().toISOString() } : {}),
  };
  const { data, error } = await supabase
    .from("tickets")
    .update(patch)
    .eq("id", id)
    .select()
    .single();
  throw_if(error, "Erro ao atualizar estado");
  return mapTicket(data);
}

// ─── DOCUMENTS ────────────────────────────────────────────────────────────────

export async function fetchDocuments(condominiumId) {
  let q = supabase
    .from("documents")
    .select("*")
    .order("created_at", { ascending: false });
  if (condominiumId) q = q.eq("condominium_id", condominiumId);
  const { data, error } = await q;
  throw_if(error, "Erro ao carregar documentos");
  return data.map(mapDoc);
}

export async function createDocument(fileObj, { name, category, tags, uploadedBy, condominiumId }) {
  let file_url = null;
  let size = null;
  let extracted_text = null;

  if (fileObj) {
    const prefix = condominiumId ? `${condominiumId}/` : "";
    const path = `${prefix}docs/${Date.now()}-${fileObj.name}`;
    const { error: upErr } = await supabase.storage
      .from("documents")
      .upload(path, fileObj, { upsert: false });
    throw_if(upErr, "Erro ao fazer upload do ficheiro");
    file_url = path; // store path, not public URL (bucket is private)
    size = fileObj.size;
    try {
      const { extractDocumentText } = await import("../utils/docText");
      extracted_text = (await extractDocumentText(fileObj)) || null;
    } catch (_) { /* extração best-effort */ }
  }

  const row = {
    name,
    category,
    tags,
    uploaded_by: uploadedBy,
    file_url,
    size,
    extracted_text,
    date: new Date().toISOString().slice(0, 10),
  };
  if (condominiumId) row.condominium_id = condominiumId;

  const { data, error } = await supabase
    .from("documents")
    .insert(row)
    .select()
    .single();
  throw_if(error, "Erro ao criar documento");
  return mapDoc(data);
}

export async function updateDocument(id, { name, category, tags, fileObj }) {
  let extraPatch = {};

  if (fileObj) {
    const path = `docs/${Date.now()}-${fileObj.name}`;
    const { error: upErr } = await supabase.storage
      .from("documents")
      .upload(path, fileObj, { upsert: false });
    throw_if(upErr, "Erro ao fazer upload do ficheiro");
    let extracted_text = null;
    try {
      const { extractDocumentText } = await import("../utils/docText");
      extracted_text = (await extractDocumentText(fileObj)) || null;
    } catch (_) { /* best-effort */ }
    extraPatch = { file_url: path, size: fileObj.size, extracted_text }; // store path, not public URL
  }

  const { data, error } = await supabase
    .from("documents")
    .update({ name, category, tags, ...extraPatch })
    .eq("id", id)
    .select()
    .single();
  throw_if(error, "Erro ao atualizar documento");
  return mapDoc(data);
}

export async function deleteDocument(id) {
  const { error } = await supabase.from("documents").delete().eq("id", id);
  throw_if(error, "Erro ao remover documento");
}

/** Reprocessa documentos sem texto extraído: descarrega, extrai e grava.
 *  onProgress(done, total) é chamado ao longo do processo.
 *  Devolve { processed, updated, failed }. */
export async function reindexDocuments(condominiumId, onProgress) {
  let q = supabase.from("documents")
    .select("id, name, file_url, extracted_text, archived")
    .not("file_url", "is", null);
  if (condominiumId) q = q.eq("condominium_id", condominiumId);
  const { data, error } = await q;
  throw_if(error, "Erro ao carregar documentos");

  const pending = (data || []).filter(d => !d.extracted_text);
  const total = pending.length;
  let done = 0, updated = 0, failed = 0;
  onProgress?.(0, total);

  const { extractDocumentText } = await import("../utils/docText");

  for (const d of pending) {
    try {
      const url = await getDownloadUrl("documents", d.file_url);
      const res = await fetch(url);
      const blob = await res.blob();
      const file = new File([blob], d.name || "documento", { type: blob.type });
      const text = await extractDocumentText(file);
      if (text) {
        const { error: upErr } = await supabase
          .from("documents").update({ extracted_text: text }).eq("id", d.id);
        if (!upErr) updated++; else failed++;
      }
    } catch (_) {
      failed++;
    }
    done++;
    onProgress?.(done, total);
  }
  return { processed: total, updated, failed };
}

/** Generates a 1-hour signed URL for a private Storage file.
 *  bucket: "documents" | "avatars"
 *  path: the storage path stored in the DB (e.g. "docs/1234-file.pdf")
 */
export async function getDownloadUrl(bucket, path) {
  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, 3600); // expires in 1 hour
  throw_if(error, "Erro ao gerar link de download");
  return data.signedUrl;
}

// ─── SUPPLIERS ────────────────────────────────────────────────────────────────

export async function fetchSuppliers(condominiumId) {
  let q = supabase
    .from("suppliers")
    .select("*")
    .order("name");
  if (condominiumId) q = q.eq("condominium_id", condominiumId);
  const { data, error } = await q;
  throw_if(error, "Erro ao carregar fornecedores");
  return data.map(mapSupplier);
}

export async function createSupplier(form) {
  const row = {
    name:    form.name,
    service: form.service || "Outro(s)",
    address: form.address || null,
    phone:   form.phone   || null,
    email:   form.email   || null,
    notes:   form.notes   || null,
  };
  if (form.condominiumId) row.condominium_id = form.condominiumId;

  const { data, error } = await supabase
    .from("suppliers")
    .insert(row)
    .select()
    .single();
  throw_if(error, "Erro ao criar fornecedor");
  return mapSupplier(data);
}

export async function updateSupplier(id, form) {
  const { data, error } = await supabase
    .from("suppliers")
    .update({
      name:    form.name,
      service: form.service || "Outro(s)",
      address: form.address || null,
      phone:   form.phone   || null,
      email:   form.email   || null,
      notes:   form.notes   || null,
    })
    .eq("id", id)
    .select()
    .single();
  throw_if(error, "Erro ao atualizar fornecedor");
  return mapSupplier(data);
}

export async function deleteSupplier(id) {
  const { error } = await supabase.from("suppliers").delete().eq("id", id);
  throw_if(error, "Erro ao remover fornecedor");
}

// ─── PROFILES ─────────────────────────────────────────────────────────────────

export async function fetchProfile(uid) {
  const { data, error } = await supabase
    .from("profiles")
    .select("*")
    .eq("id", uid)
    .single();
  throw_if(error, "Erro ao carregar perfil");
  return mapProfile(data);
}

export async function fetchAllProfiles(condominiumId) {
  if (condominiumId) {
    // Fetch users who are members of this condominium
    const { data, error } = await supabase
      .from("condominium_users")
      .select("*, profiles(*)")
      .eq("condominium_id", condominiumId);
    throw_if(error, "Erro ao carregar utilizadores");
    return data
      .filter(cu => cu.profiles) // guard against nulls
      .map(cu => ({
        ...mapProfile(cu.profiles),
        role:       cu.role,         // per-condo role overrides profile role
        fracao:     cu.fracao,       // per-condo fracao overrides profile fracao
        permilagem: cu.permilagem ?? null,
      }))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }
  // Fallback: no condo filter (backward compat)
  const { data, error } = await supabase
    .from("profiles")
    .select("*")
    .order("name");
  throw_if(error, "Erro ao carregar utilizadores");
  return data.map(mapProfile);
}

export async function updateOwnProfile(uid, { name, phone }, photoFile, photoRemoved = false) {
  let photo_url; // undefined = no change

  if (photoFile) {
    const ext = photoFile.name.split(".").pop();
    const path = `${uid}/avatar.${ext}`;
    const { error: upErr } = await supabase.storage
      .from("avatars")
      .upload(path, photoFile, { upsert: true });
    throw_if(upErr, "Erro ao fazer upload da foto");
    const { data: urlData } = supabase.storage.from("avatars").getPublicUrl(path);
    photo_url = `${urlData.publicUrl}?t=${Date.now()}`;
  } else if (photoRemoved) {
    photo_url = null; // explicitly clear
  }

  const patch = { name: name.trim(), phone: phone.trim() || null };
  if (photo_url !== undefined) patch.photo_url = photo_url;

  const { data, error } = await supabase
    .from("profiles")
    .update(patch)
    .eq("id", uid)
    .select()
    .single();
  throw_if(error, "Erro ao atualizar perfil");
  return mapProfile(data);
}

// ─── ADMIN — User management ─────────────────────────────────────────────────

/** Creates a new user via Vercel serverless function (auto-generates temp password + sends email). */
export async function adminCreateUser({ name, email, phone, fracao, permilagem, role, condominiumId, sendEmail = true }) {
  const res = await fetch("/api/create-user", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, email, phone, fracao, permilagem, role, condominiumId, sendEmail }),
  });

  const json = await res.json();
  if (!res.ok) throw new Error(json.error || "Erro ao criar utilizador");
  return json; // { id, email, emailSent }
}

/** Updates profile fields (and optionally photo) for any user — admin only, enforced by RLS. */
export async function adminUpdateProfile(id, patch, photoFile, photoRemoved = false, condominiumId = null) {
  let photo_url; // undefined = no change

  if (photoFile) {
    const ext = photoFile.name.split(".").pop();
    const path = `${id}/avatar.${ext}`;
    const { error: upErr } = await supabase.storage
      .from("avatars")
      .upload(path, photoFile, { upsert: true });
    throw_if(upErr, "Erro ao fazer upload da foto");
    const { data: urlData } = supabase.storage.from("avatars").getPublicUrl(path);
    photo_url = `${urlData.publicUrl}?t=${Date.now()}`;
  } else if (photoRemoved) {
    photo_url = null; // explicitly clear
  }

  // permilagem is only in condominium_users, not in profiles — strip it before updating profiles
  const { permilagem: _perm, ...profilePatch } = patch;
  const fullPatch = { ...profilePatch };
  if (photo_url !== undefined) fullPatch.photo_url = photo_url;

  const { data, error } = await supabase
    .from("profiles")
    .update(fullPatch)
    .eq("id", id)
    .select()
    .single();
  throw_if(error, "Erro ao atualizar utilizador");

  // Sync role, fracao and permilagem to condominium_users — source of truth on login
  if (condominiumId && (patch.role !== undefined || patch.fracao !== undefined || patch.permilagem !== undefined)) {
    const cuPatch = {};
    if (patch.role       !== undefined) cuPatch.role       = patch.role;
    if (patch.fracao     !== undefined) cuPatch.fracao     = patch.fracao;
    if (patch.permilagem !== undefined) cuPatch.permilagem = patch.permilagem;
    const { error: cuErr } = await supabase
      .from("condominium_users")
      .update(cuPatch)
      .eq("user_id", id)
      .eq("condominium_id", condominiumId);
    if (cuErr) console.error("condominium_users sync error:", cuErr);
    throw_if(cuErr, "Erro ao atualizar dados no condomínio");
  }

  return mapProfile(data);
}

/** Resets a user's password via serverless function (generates temp pw, updates auth, sends email). */
export async function adminResetPassword(email) {
  const res = await fetch("/api/reset-password", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email: email.trim() }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Erro ao enviar email de recuperação");
  }
}

/** Recovers password from the login page. Calls the same serverless function.
 *  Never reveals whether the email exists (always succeeds from the user's perspective). */
export async function recoverPassword(email) {
  const res = await fetch("/api/reset-password", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email: email.trim() }),
  });
  // Always return true — don't reveal if email exists
  if (!res.ok) console.warn("Recovery API error:", res.status);
  return true;
}

// ─── COCKPIT — Super Admin ──────────────────────────────────────────────────

/** Builds accessStats (scalars + chart arrays) from raw access_logs rows. */
function buildAccessStats(rows, superAdminIds, nowDate, onlineCount) {
  // Filter out super admins
  const filtered = rows.filter(r => !superAdminIds.has(r.user_id));

  const startOfToday = new Date(nowDate.getFullYear(), nowDate.getMonth(), nowDate.getDate());
  const startOfMonth = new Date(nowDate.getFullYear(), nowDate.getMonth(), 1);
  const startOfYear  = new Date(nowDate.getFullYear(), 0, 1);

  // Scalar counts (distinct users)
  const todayIds = new Set(), monthIds = new Set(), yearIds = new Set();
  for (const r of filtered) {
    const d = new Date(r.logged_at);
    yearIds.add(r.user_id);
    if (d >= startOfMonth) monthIds.add(r.user_id);
    if (d >= startOfToday) todayIds.add(r.user_id);
  }

  // ── Daily array: last 7 days (distinct users per day) ──
  const daily = Array(7).fill(0);
  for (let i = 0; i < 7; i++) {
    const dayStart = new Date(nowDate.getFullYear(), nowDate.getMonth(), nowDate.getDate() - (6 - i));
    const dayEnd   = new Date(dayStart); dayEnd.setDate(dayEnd.getDate() + 1);
    const ids = new Set();
    for (const r of filtered) {
      const d = new Date(r.logged_at);
      if (d >= dayStart && d < dayEnd) ids.add(r.user_id);
    }
    daily[i] = ids.size;
  }

  // ── Monthly array: 4 weeks of current month (distinct users per week) ──
  const monthly = Array(4).fill(0);
  const weekSets = [new Set(), new Set(), new Set(), new Set()];
  for (const r of filtered) {
    const d = new Date(r.logged_at);
    if (d >= startOfMonth) {
      const weekIdx = Math.min(Math.floor((d.getDate() - 1) / 7), 3);
      weekSets[weekIdx].add(r.user_id);
    }
  }
  for (let i = 0; i < 4; i++) monthly[i] = weekSets[i].size;

  // ── Yearly array: 12 months (distinct users per month) ──
  const yearly = Array(12).fill(0);
  const monthSets = Array.from({ length: 12 }, () => new Set());
  for (const r of filtered) {
    const d = new Date(r.logged_at);
    if (d >= startOfYear) {
      monthSets[d.getMonth()].add(r.user_id);
    }
  }
  for (let i = 0; i < 12; i++) yearly[i] = monthSets[i].size;

  return {
    today:   todayIds.size,
    month:   monthIds.size,
    year:    yearIds.size,
    online:  onlineCount,
    daily,
    monthly,
    yearly,
  };
}

/** Loads all condominiums with aggregated stats for the super admin cockpit. */
export async function fetchAllCondosData() {
  // Each query is independent — don't let one missing table crash the whole cockpit
  // Fetch super users via serverless function (bypasses RLS to include inactive users)
  const fetchSuperUsersFromApi = async () => {
    try {
      const res = await fetch("/api/super-users");
      if (res.ok) {
        const json = await res.json();
        return { data: json.data || [], error: null };
      }
    } catch (_) { /* fall through */ }
    // Fallback to direct query if serverless function unavailable (e.g. local dev)
    return supabase.from("profiles").select("*").eq("is_super_admin", true).order("name").then(r => r, () => ({ data: [], error: null }));
  };

  // Date boundaries for access_logs
  const nowDate = new Date();
  const startOfYear  = new Date(nowDate.getFullYear(), 0, 1).toISOString();

  // Online threshold: users with access_log in the last 30 minutes
  const ONLINE_THRESHOLD_MS = 30 * 60 * 1000;
  const onlineSince = new Date(nowDate.getTime() - ONLINE_THRESHOLD_MS).toISOString();

  const [condosRes, preRegistosRes, pedidosRes, superUsersRes, condoUsersRes, allProfilesRes, ticketsRes, docsRes, accessYearRes, accessOnlineRes] = await Promise.all([
    supabase.from("condominiums").select("*").order("name").then(r => r, () => ({ data: [], error: null })),
    supabase.from("pre_registos").select("*").order("date", { ascending: false }).then(r => r, () => ({ data: [], error: null })),
    supabase.from("pedidos_info").select("*").order("date", { ascending: false }).then(r => r, () => ({ data: [], error: null })),
    fetchSuperUsersFromApi(),
    supabase.from("condominium_users").select("condominium_id, role").then(r => r, () => ({ data: [], error: null })),
    supabase.from("profiles").select("id, is_super_admin, active").eq("is_super_admin", false).then(r => r, () => ({ data: [], error: null })),
    supabase.from("tickets").select("id, condominium_id, status").then(r => r, () => ({ data: [], error: null })),
    supabase.from("documents").select("id, condominium_id").then(r => r, () => ({ data: [], error: null })),
    // Access logs — full year with timestamps for chart aggregation
    supabase.from("access_logs").select("user_id, logged_at").gte("logged_at", startOfYear).order("logged_at", { ascending: true }).then(r => r, () => ({ data: [], error: null })),
    // Online — access_log within last 30 min
    supabase.from("access_logs").select("user_id").gte("logged_at", onlineSince).then(r => r, () => ({ data: [], error: null })),
  ]);

  const preRegistos = (preRegistosRes.data || []).map(r => ({
    id: r.id, email: r.email, date: r.date, status: r.status,
  }));

  const pedidosInfo = (pedidosRes.data || []).map(r => ({
    id: r.id, nome: r.nome, email: r.email, telefone: r.telefone,
    mensagem: r.mensagem, date: r.date, status: r.status,
  }));

  // Build per-condo aggregation maps
  const condoUsers = condoUsersRes.data || [];
  const allTickets = ticketsRes.data || [];
  const allDocs = docsRes.data || [];
  const allProfiles = allProfilesRes.data || [];

  const usersByCondoMap = {};
  const adminsByCondoMap = {};
  for (const cu of condoUsers) {
    usersByCondoMap[cu.condominium_id] = (usersByCondoMap[cu.condominium_id] || 0) + 1;
    if (cu.role === "admin") adminsByCondoMap[cu.condominium_id] = (adminsByCondoMap[cu.condominium_id] || 0) + 1;
  }

  const ticketsByCondoMap = {};
  const ticketsAbertosByCondoMap = {};
  const ticketsAtivosByCondoMap = {};
  for (const t of allTickets) {
    ticketsByCondoMap[t.condominium_id] = (ticketsByCondoMap[t.condominium_id] || 0) + 1;
    if (t.status === "Aberto") ticketsAbertosByCondoMap[t.condominium_id] = (ticketsAbertosByCondoMap[t.condominium_id] || 0) + 1;
    if (t.status === "Aberto" || t.status === "Em tratamento") ticketsAtivosByCondoMap[t.condominium_id] = (ticketsAtivosByCondoMap[t.condominium_id] || 0) + 1;
  }

  const docsByCondoMap = {};
  for (const d of allDocs) {
    docsByCondoMap[d.condominium_id] = (docsByCondoMap[d.condominium_id] || 0) + 1;
  }

  // Total non-super-admin users
  const totalUsers = allProfiles.length;

  const superUsersData = (superUsersRes.data || []);

  // Online: users with access_log in last 30 min, excluding super admins
  const superAdminIds = new Set(superUsersData.map(u => u.id));
  const onlineUserIds = new Set((accessOnlineRes.data || []).map(r => r.user_id));
  // Remove super admin IDs from online set
  for (const saId of superAdminIds) onlineUserIds.delete(saId);
  const onlineCount = onlineUserIds.size;

  // Filter out the demo condominium — it's static and not manageable
  return {
    condos: (condosRes.data || [])
      .filter(c => c.name !== DEMO_CONDO_NAME)
      .map(c => ({
        ...mapCondo(c),
        ticketsAbertos: ticketsAbertosByCondoMap[c.id] || 0,
        ticketsAtivos: ticketsAtivosByCondoMap[c.id] || 0,
        totalUsers: usersByCondoMap[c.id] || 0,
        totalAdmins: adminsByCondoMap[c.id] || 0,
        totalDocs: docsByCondoMap[c.id] || 0,
        totalTickets: ticketsByCondoMap[c.id] || 0,
        activity: (ticketsAtivosByCondoMap[c.id] || 0) + (docsByCondoMap[c.id] || 0),
        activityByPeriod: { week: 0, month: 0, year: 0 },
      })),
    totalUsers,
    superUsers: superUsersData.map(mapProfile),
    preRegistos,
    pedidosInfo,
    accessStats: buildAccessStats(accessYearRes.data || [], superAdminIds, nowDate, onlineCount),
  };
}

/** Submits an early-access email (pre-registo) from the landing page. */
export async function submitPreRegisto(email) {
  const trimmed = email.trim().toLowerCase();

  // Check if this email already has a pre-registo
  const { data: existing } = await supabase
    .from("pre_registos")
    .select("id, status")
    .eq("email", trimmed)
    .maybeSingle();

  if (existing) {
    throw new Error("Este email já foi registado para pré-acesso.");
  }

  const { data, error } = await supabase
    .from("pre_registos")
    .insert({ email: trimmed, status: "novo", date: new Date().toISOString().slice(0, 10) })
    .select()
    .single();
  throw_if(error, "Erro ao registar email");
  return data;
}

/** Submits a "pedir informações" request from the landing page. */
export async function submitPedidoInfo({ nome, email, telefone, mensagem }) {
  const { data, error } = await supabase
    .from("pedidos_info")
    .insert({
      nome: nome.trim(),
      email: email.trim(),
      telefone: telefone?.trim() || null,
      mensagem: mensagem.trim(),
      status: "novo",
      date: new Date().toISOString().slice(0, 10),
    })
    .select()
    .single();
  throw_if(error, "Erro ao submeter pedido");
  return data;
}

/** Updates the status of a pedido de informação. */
export async function updatePedidoStatus(id, status) {
  const { error } = await supabase
    .from("pedidos_info")
    .update({ status })
    .eq("id", id);
  throw_if(error, "Erro ao atualizar pedido");
}

/** Creates a new condominium. */
export async function createCondominium({ name, morada }) {
  const { data, error } = await supabase
    .from("condominiums")
    .insert({ name, morada, num_fracoes: 0 })
    .select()
    .single();
  throw_if(error, "Erro ao criar condomínio");
  return mapCondo(data);
}

/** Updates a pre-registo status. */
export async function updatePreRegistoStatus(id, status) {
  const { error } = await supabase
    .from("pre_registos")
    .update({ status })
    .eq("id", id);
  throw_if(error, "Erro ao atualizar pré-registo");
}

/** Deletes a pre-registo by id. */
export async function deletePreRegisto(id) {
  const { error } = await supabase.from("pre_registos").delete().eq("id", id);
  throw_if(error, "Erro ao eliminar pré-registo");
}

/** Deletes a pedido de informação by id. */
export async function deletePedidoInfo(id) {
  const { error } = await supabase.from("pedidos_info").delete().eq("id", id);
  throw_if(error, "Erro ao eliminar pedido");
}

// ─── SUPER USERS ──────────────────────────────────────────────────────────────

/** Generates a readable temporary password (12 chars, letters + digits + symbol). */
function generateTempPassword() {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghjkmnpqrstuvwxyz";
  const digits = "23456789";
  const pick = (s) => s[Math.floor(Math.random() * s.length)];
  // Ensure at least 1 upper, 1 lower, 1 digit, 1 symbol
  let pw = [pick(upper), pick(lower), pick(digits), "!"];
  for (let i = 0; i < 8; i++) pw.push(pick(upper + lower + digits));
  // Shuffle
  for (let i = pw.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pw[i], pw[j]] = [pw[j], pw[i]];
  }
  return pw.join("");
}

/** Sends a welcome email with temp password via Vercel serverless function. */
async function sendWelcomeEmail({ to, name, tempPassword }) {
  try {
    const res = await fetch("/api/send-email", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ to, name, tempPassword }),
    });
    if (!res.ok) {
      const errBody = await res.json().catch(() => ({}));
      console.warn("Email API error:", res.status, errBody);
      return false;
    }
    return true;
  } catch (err) {
    console.warn("Erro ao enviar email:", err);
    return false;
  }
}

/** Creates a new super admin user via signUp (no edge function). */
export async function createSuperUser({ name, email, phone }) {
  // Temporary client so the current super admin session is not affected
  const tempClient = createClient(
    import.meta.env.VITE_SUPABASE_URL,
    import.meta.env.VITE_SUPABASE_ANON_KEY,
    { auth: { persistSession: false, autoRefreshToken: false } }
  );

  // Readable temporary password
  const tempPassword = generateTempPassword();

  const { data: signUpData, error: signUpError } = await tempClient.auth.signUp({
    email,
    password: tempPassword,
    options: { data: { name, role: "admin" } },
  });
  if (signUpError) throw new Error(signUpError.message);
  if (!signUpData.user) throw new Error("Erro ao criar utilizador");

  const newUserId = signUpData.user.id;

  // Wait briefly for any DB trigger
  await new Promise(r => setTimeout(r, 500));

  // Check if tempClient got a session from signUp
  const hasSession = !!signUpData.session;
  const writeClient = hasSession ? tempClient : supabase;

  // Upsert profile with must_change_password flag
  const { error: profileError } = await writeClient
    .from("profiles")
    .upsert({
      id: newUserId,
      name: name.trim(),
      email: email.trim(),
      phone: phone || null,
      role: "admin",
      is_super_admin: true,
      active: true,
      must_change_password: true,
    }, { onConflict: "id" });
  if (profileError) throw new Error("Utilizador criado mas erro ao guardar perfil: " + profileError.message);

  // Send welcome email with temp password via Resend
  const emailSent = await sendWelcomeEmail({ to: email.trim(), name: name.trim(), tempPassword });
  if (!emailSent) console.warn("Aviso: email de boas-vindas não enviado. O utilizador foi criado com a password temporária.");

  // Re-fetch profile
  const { data: updatedProfile } = await writeClient
    .from("profiles")
    .select("*")
    .eq("id", newUserId)
    .maybeSingle();

  if (updatedProfile) return mapProfile(updatedProfile);

  return {
    id: newUserId,
    name: name.trim(),
    email: email.trim(),
    phone: phone || null,
    fracao: null,
    role: "admin",
    photo: null,
    active: true,
    isSuperAdmin: true,
    mustChangePassword: true,
  };
}

/** Changes password for current user and clears must_change_password flag.
 *  Also converts pre-registo from "convidado" → "convertido" on first login. */
export async function changePassword(newPassword) {
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw new Error(error.message);

  const { data: { user } } = await supabase.auth.getUser();
  if (user) {
    // Clear the must_change_password flag (await — needed before boot)
    await supabase
      .from("profiles")
      .update({ must_change_password: false })
      .eq("id", user.id);

    // Convert pre-registo — fire & forget (don't block login)
    fetch("/api/convert-invite", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: user.email }),
    }).catch(() => {});
  }
}

/** Toggles a super user's active state via serverless function (bypasses RLS). */
export async function toggleSuperUserActive(id, active) {
  const res = await fetch("/api/super-users", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "toggle", id, active }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || (active ? "Erro ao ativar utilizador" : "Erro ao desativar utilizador"));
  }
}

/** Updates a super user's profile fields via serverless function (bypasses RLS). */
export async function updateSuperUser(id, { name, phone }, photoFile, photoRemoved = false) {
  let photo_url;

  if (photoFile) {
    const ext = photoFile.name.split(".").pop();
    const path = `${id}/avatar.${ext}`;
    const { error: upErr } = await supabase.storage
      .from("avatars")
      .upload(path, photoFile, { upsert: true });
    throw_if(upErr, "Erro ao fazer upload da foto");
    const { data: urlData } = supabase.storage.from("avatars").getPublicUrl(path);
    photo_url = `${urlData.publicUrl}?t=${Date.now()}`;
  } else if (photoRemoved) {
    photo_url = null;
  }

  const patch = { name: name.trim(), phone: phone?.trim() || null };
  if (photo_url !== undefined) patch.photo_url = photo_url;

  const res = await fetch("/api/super-users", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "update", id, patch }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Erro ao atualizar super administrador");
  }
  const { data } = await res.json();
  return mapProfile(data);
}

/** Updates a condominium's name and/or morada. */
export async function updateCondominium(id, { name, morada }) {
  const { data, error } = await supabase
    .from("condominiums")
    .update({ name, morada })
    .eq("id", id)
    .select()
    .single();
  throw_if(error, "Erro ao atualizar condomínio");
  return mapCondo(data);
}

// ─── ACCESS LOGS (session tracking) ──────────────────────────────────────────

/** Logs a user access (call once per login / session restore). */
export async function logAccess(userId) {
  const { error } = await supabase
    .from("access_logs")
    .insert({ user_id: userId });
  if (error) console.warn("logAccess error:", error.message);
}

/** Toggles a condominium active/inactive. */
export async function toggleCondoActive(id, active) {
  const { error } = await supabase
    .from("condominiums")
    .update({ active })
    .eq("id", id);
  throw_if(error, active ? "Erro ao reativar condomínio" : "Erro ao desativar condomínio");
}

// ─── NOTIFICATIONS ────────────────────────────────────────────────────────────

const mapNotification = (r) => ({
  id:            r.id,
  condominiumId: r.condominium_id,
  title:         r.title,
  description:   r.description,
  type:          r.type,
  startDate:     r.start_date,
  endDate:       r.end_date,
  fracao:        r.fracao,
  authorId:      r.author_id,
  authorName:    r.author_name,
  createdAt:     r.created_at,
});

export async function fetchNotifications(condominiumId) {
  let q = supabase
    .from("notifications")
    .select("*")
    .order("start_date", { ascending: true });
  if (condominiumId) q = q.eq("condominium_id", condominiumId);
  const { data, error } = await q;
  throw_if(error, "Erro ao carregar notificações");
  return data.map(mapNotification);
}

export async function createNotification({ title, description, type, startDate, endDate, fracao, authorId, authorName, condominiumId }) {
  const row = {
    title,
    description: description || null,
    type,
    start_date: startDate,
    end_date: endDate,
    fracao,
    author_id: authorId,
    author_name: authorName,
  };
  if (condominiumId) row.condominium_id = condominiumId;

  const { data, error } = await supabase
    .from("notifications")
    .insert(row)
    .select()
    .single();
  throw_if(error, "Erro ao criar notificação");
  return mapNotification(data);
}

export async function deleteNotification(id) {
  const { error } = await supabase.from("notifications").delete().eq("id", id);
  throw_if(error, "Erro ao eliminar notificação");
}

// ─── BUDGET ──────────────────────────────────────────────────────────────────

const mapBudget = (r) => ({
  id:             r.id,
  condominiumId:  r.condominium_id,
  year:           r.year,
  startDate:      r.start_date || null,
  endDate:        r.end_date || null,
  openingReserve: r.opening_reserve != null ? parseFloat(r.opening_reserve) : 0,
  reserveUsePct:  r.reserve_use_pct != null ? parseFloat(r.reserve_use_pct) : 0,
  notes:          r.notes || null,
  createdAt:      r.created_at,
});

const mapBudgetItem = (r) => ({
  id:                r.id,
  budgetId:          r.budget_id,
  category:          r.category,
  description:       r.description,
  amount:            parseFloat(r.amount),
  type:              r.type || "despesa",
  revenueDestination: r.revenue_destination || null,
  alivioPct:         r.alivio_pct != null ? parseFloat(r.alivio_pct) : 0,
  isExtraQuota:      r.is_extra_quota === true,
  createdAt:         r.created_at,
});

/** Fetches the budget + items for a given condominium and year. */
export async function fetchBudget(condominiumId, year) {
  const { data: budget, error: bErr } = await supabase
    .from("budgets")
    .select("*")
    .eq("condominium_id", condominiumId)
    .eq("year", year)
    .maybeSingle();
  throw_if(bErr, "Erro ao carregar orçamento");
  if (!budget) return { budget: null, items: [] };

  const { data: items, error: iErr } = await supabase
    .from("budget_items")
    .select("*")
    .eq("budget_id", budget.id)
    .order("created_at", { ascending: true });
  throw_if(iErr, "Erro ao carregar rubricas");

  return { budget: mapBudget(budget), items: (items || []).map(mapBudgetItem) };
}

/** Updates budget-level settings (opening reserve, reserve-use %). */
export async function updateBudget(id, patch) {
  const row = {};
  if (patch.openingReserve != null) row.opening_reserve = patch.openingReserve;
  if (patch.reserveUsePct != null) row.reserve_use_pct = patch.reserveUsePct;
  if (patch.startDate != null) row.start_date = patch.startDate;
  if (patch.endDate != null) row.end_date = patch.endDate;
  if (patch.year != null) row.year = patch.year;
  const { data, error } = await supabase
    .from("budgets").update(row).eq("id", id).select().single();
  throw_if(error, "Erro ao atualizar orçamento");
  return mapBudget(data);
}

/** Creates an empty budget for a fiscal exercise (period + opening reserve). */
export async function createBudget(condominiumId, year, { startDate = null, endDate = null, openingReserve = 0 } = {}) {
  const { data, error } = await supabase
    .from("budgets")
    .insert({
      condominium_id: condominiumId,
      year,
      start_date: startDate,
      end_date: endDate,
      opening_reserve: openingReserve,
    })
    .select()
    .single();
  throw_if(error, "Erro ao criar orçamento");
  return mapBudget(data);
}

/** Lists existing exercises (desc) with year + period + opening reserve. */
export async function fetchBudgetYears(condominiumId) {
  const { data, error } = await supabase
    .from("budgets")
    .select("year, start_date, end_date, opening_reserve")
    .eq("condominium_id", condominiumId)
    .order("year", { ascending: false });
  throw_if(error, "Erro ao carregar exercícios");
  return (data || []).map(r => ({
    year: r.year,
    startDate: r.start_date || null,
    endDate: r.end_date || null,
    openingReserve: r.opening_reserve != null ? parseFloat(r.opening_reserve) : 0,
  }));
}

/** Creates a budget for targetYear copying all rubrics from sourceYear. */
export async function copyBudget(condominiumId, targetYear, sourceYear, period = {}) {
  const { items } = await fetchBudget(condominiumId, sourceYear);
  const budget = await createBudget(condominiumId, targetYear, period);
  if (items.length) {
    const rows = items.map(i => ({
      budget_id: budget.id,
      category: i.category,
      description: i.description,
      amount: i.amount,
      type: i.type,
      alivio_pct: i.type === "receita" ? i.alivioPct : 0,
      revenue_destination: null,
      is_extra_quota: i.isExtraQuota === true,
    }));
    const { error } = await supabase.from("budget_items").insert(rows);
    throw_if(error, "Erro ao copiar rubricas");
  }
  return budget;
}

/** Deletes a budget and all its line items (and extra-quota payments). */
export async function deleteBudget(id) {
  await supabase.from("budget_items").delete().eq("budget_id", id);
  const { error } = await supabase.from("budgets").delete().eq("id", id);
  throw_if(error, "Erro ao eliminar orçamento");
}

/** Adds a line item to a budget. */
export async function createBudgetItem(budgetId, { category, description, amount, type = "despesa", alivioPct = 0, isExtraQuota = false }) {
  const { data, error } = await supabase
    .from("budget_items")
    .insert({
      budget_id: budgetId, category, description, amount,
      type,
      alivio_pct: type === "receita" ? alivioPct : 0,
      revenue_destination: null,
      is_extra_quota: isExtraQuota,
    })
    .select()
    .single();
  throw_if(error, "Erro ao adicionar rubrica");
  return mapBudgetItem(data);
}

/** Updates a budget line item. */
export async function updateBudgetItem(id, { category, description, amount, type = "despesa", alivioPct = 0 }) {
  const { data, error } = await supabase
    .from("budget_items")
    .update({
      category, description, amount,
      type,
      alivio_pct: type === "receita" ? alivioPct : 0,
      revenue_destination: null,
    })
    .eq("id", id)
    .select()
    .single();
  throw_if(error, "Erro ao atualizar rubrica");
  return mapBudgetItem(data);
}

/** Deletes a budget line item. */
export async function deleteBudgetItem(id) {
  const { error } = await supabase.from("budget_items").delete().eq("id", id);
  throw_if(error, "Erro ao eliminar rubrica");
}

// ─── QUOTAS ──────────────────────────────────────────────────────────────────

/** Fetches quota settings (reserve %) for a given condominium and year. */
export async function fetchQuotaSettings(condominiumId, year) {
  const { data, error } = await supabase
    .from("quota_settings")
    .select("*")
    .eq("condominium_id", condominiumId)
    .eq("year", year)
    .maybeSingle();
  throw_if(error, "Erro ao carregar configurações de quotas");
  return data
    ? { id: data.id, reservePct: parseFloat(data.reserve_pct) }
    : { id: null, reservePct: 0 };
}

/** Upserts quota settings for a given condominium and year. */
export async function saveQuotaSettings(condominiumId, year, reservePct) {
  const { error } = await supabase
    .from("quota_settings")
    .upsert(
      { condominium_id: condominiumId, year, reserve_pct: reservePct, updated_at: new Date().toISOString() },
      { onConflict: "condominium_id,year" }
    );
  throw_if(error, "Erro ao guardar configurações de quotas");
}

/** Fetches all quota payments for a condominium and year. */
export async function fetchQuotaPayments(condominiumId, year) {
  const { data, error } = await supabase
    .from("quota_payments")
    .select("*")
    .eq("condominium_id", condominiumId)
    .eq("year", year);
  throw_if(error, "Erro ao carregar pagamentos");
  return (data || []).map(r => ({
    id:            r.id,
    condominiumId: r.condominium_id,
    userId:        r.user_id,
    year:          r.year,
    month:         r.month,
    amount:        parseFloat(r.amount),
    paid:          r.paid,
    paidAt:        r.paid_at,
    notes:         r.notes,
  }));
}

// ─── CONDO SETTINGS ──────────────────────────────────────────────────────────

/** Fetches PayPal email for a condominium. */
export async function fetchCondoPaypalEmail(condominiumId) {
  const { data, error } = await supabase
    .from("condominiums")
    .select("paypal_email")
    .eq("id", condominiumId)
    .single();
  throw_if(error, "Erro ao carregar configuração PayPal");
  return data?.paypal_email || null;
}

/** Saves PayPal email for a condominium. */
export async function saveCondoPaypalEmail(condominiumId, paypalEmail) {
  const { error } = await supabase
    .from("condominiums")
    .update({ paypal_email: paypalEmail?.trim() || null })
    .eq("id", condominiumId);
  throw_if(error, "Erro ao guardar configuração PayPal");
}

/** Marks a quota payment as paid or unpaid. */
export async function setQuotaPayment(condominiumId, userId, year, month, amount, paid) {
  const { error } = await supabase
    .from("quota_payments")
    .upsert(
      {
        condominium_id: condominiumId,
        user_id: userId,
        year, month, amount,
        paid,
        paid_at: paid ? new Date().toISOString() : null,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "condominium_id,user_id,year,month" }
    );
  throw_if(error, "Erro ao registar pagamento");
}

/** Fetches extra-quota payments for a condo/year. */
export async function fetchExtraQuotaPayments(condominiumId, year) {
  const { data, error } = await supabase
    .from("extra_quota_payments")
    .select("*")
    .eq("condominium_id", condominiumId)
    .eq("year", year);
  throw_if(error, "Erro ao carregar pagamentos de quotas extra");
  return (data || []).map(r => ({
    userId:       r.user_id,
    budgetItemId: r.budget_item_id,
    amount:       parseFloat(r.amount),
    paid:         r.paid,
  }));
}

/** Upserts an extra-quota payment (keyed by rubric + fraction). */
export async function setExtraQuotaPayment(condominiumId, userId, budgetItemId, year, amount, paid) {
  const { error } = await supabase
    .from("extra_quota_payments")
    .upsert(
      {
        condominium_id: condominiumId,
        user_id: userId,
        budget_item_id: budgetItemId,
        year, amount,
        paid,
        paid_at: paid ? new Date().toISOString() : null,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "budget_item_id,user_id" }
    );
  throw_if(error, "Erro ao registar pagamento de quota extra");
}
